import { message } from "antd";
import { useContext, useMemo } from "react";
import { deviceDetect, deviceType } from "react-device-detect";
import ReactGA from "react-ga4";
import AuthContext from "../context/AuthContext";
import { requestForToken } from "../firebase";
import { httpRequest } from "../helpers/api";
import { getLoginData, removeToken, saveToken } from "../helpers/auth";
import { getErrorMessage } from "../helpers/errorHandler";
import { IHttpResponse } from "../helpers/pagination";
import { EAuthStatus } from "../types/auth.type";
import { EEmployeeRole, EMPLOYEE_ROLES_LEVELS, EMPLOYEE_ROLES_MAPPING, initialEmployee } from "../types/employee.type";
import { OfficeProperties } from "../types/office.type";
import { initialUser, UserProperties } from "../types/user.type";

const HQ_MANAGER_PLUS_ROLES = [EEmployeeRole.admin, EEmployeeRole.admin_general_manager, EEmployeeRole.admin_manager];

export const HQ_MANAGEMENT_ROLES = [...HQ_MANAGER_PLUS_ROLES, EEmployeeRole.admin_hq];

const HQ_MANAGEMENT_ROLES_EXC_SA = [
	EEmployeeRole.admin_general_manager,
	EEmployeeRole.admin_manager,
	EEmployeeRole.admin_hq,
];

export const HQ_FINANCE_ROLES = [
	EEmployeeRole.admin_finance_gm,
	EEmployeeRole.admin_finance_manager,
	EEmployeeRole.admin_finance_staff,
];

export const HQ_BLOG_ROLES = [...HQ_MANAGEMENT_ROLES, EEmployeeRole.admin_blog];
export const OFFICE_MANAGEMENT_ROLES = [
	EEmployeeRole.franchise_owner,
	EEmployeeRole.business_owner,
	EEmployeeRole.manager_office,
	EEmployeeRole.admin_office,
	EEmployeeRole.finance_office,
];

const OFFICE_MANAGEMENT_ROLES2 = [EEmployeeRole.franchise_owner, EEmployeeRole.business_owner];

export default function useAuth() {
	const { auth, setAuth, isLoading, setIsLoading } = useContext(AuthContext);

	const saveProfile = async (data: UserProperties) => {
		setIsLoading(() => true);

		const formData = {
			name: data.name,
			email: data.email,
			phone: data.phone,
			userType: data.userType,
			status: data.status,

			firstName: data.firstName,
			middleName: data.middleName,
			lastName: data.lastName,
		};

		try {
			const resultUpdate = await httpRequest.patch<IHttpResponse<UserProperties>>(
				"/users/" + auth.user.userId,
				formData
			);
			setAuth({
				...auth,
				user: resultUpdate.data.payload,
			});
			setIsLoading(() => false);
			message.success("Update profile successfully");
		} catch (err) {
			message.error(getErrorMessage(err));
			setIsLoading(() => false);
		}
	};

	const login = async (data: { email: string; password: string }) => {
		try {
			setIsLoading(() => true);
			const resultAuthLogin = await httpRequest.post<
				IHttpResponse<{
					token: string;
				}>
			>(process.env.REACT_APP_BASE_URL + "/auth/signin", data);

			const loginData = await getLoginData(resultAuthLogin.data.payload.token);

			if (resultAuthLogin.data.payload.token) {
				requestForToken(async (token) => {
					if (token) {
						const userAgent = navigator.userAgent;
						console.log("deviceDetect", deviceDetect(userAgent));
						console.log("deviceType", deviceType);
						const deviceId = localStorage.getItem("deviceId");
						const device = deviceDetect(userAgent);
						const dto = {
							fcmToken: token,
							deviceId,
							deviceType: deviceType,
							meta: {
								platform: "cms-pwa",
								os: device.osName,
								osVersion: device.osVersion,
								appVersion: process.env.REACT_APP_VERSION,
							},
						};
						console.log("fcm dto::", dto);

						await httpRequest.post("/fcm/register", dto, {
							headers: {
								Authorization: `Bearer ${resultAuthLogin.data.payload.token}`,
							},
						});
					}
				});
			}

			saveToken(resultAuthLogin.data.payload.token);
			setAuth({
				status: EAuthStatus.LOGGED_IN,
				...loginData,
			});
			localStorage.setItem("auth_event", "login");
			ReactGA.set({
				user_id: loginData.user.userId,
				employee_id: loginData.employeeId,
				office_id: loginData.officeId,
			});
			message.success("Welcome " + (loginData.user.nickName ?? loginData.user.name) + "!");
			setIsLoading(() => false);
		} catch (err) {
			message.error("Login failed. " + getErrorMessage(err));
			setIsLoading(() => false);
		}
	};

	const logout = async () => {
		try {
			setIsLoading(() => true);
			const fcmToken = localStorage.getItem("fcmToken");
			if (fcmToken) {
				console.log("delete fcmToken");
				await httpRequest.delete(`/fcm/${fcmToken}`);
			}

			await httpRequest.post(process.env.REACT_APP_BASE_URL + "/auth/signout");

			localStorage.removeItem("fcmToken");
			localStorage.setItem("auth_event", "logout");

			message.success("Logout successfully.");
		} catch (err) {
			message.error("Logout failed. " + getErrorMessage(err));
			console.error(err);
		} finally {
			removeToken();
			setAuth({
				status: EAuthStatus.LOGGED_OUT,
				user: initialUser,
				employees: [initialEmployee],
				employeeId: "",
				officeId: "",
				roles: [],
				currentPosition: "",
			});
			setIsLoading(() => false);
		}
	};

	const ping = async () => {
		try {
			const res = await httpRequest.get<IHttpResponse<{ status: string }>>(
				process.env.REACT_APP_BASE_URL + "/auth/ping"
			);
			if (res.data.payload.status === "ok") {
				console.info("API Ping successful");
			} else {
				throw new Error("API Ping status not ok", { cause: res.data.payload });
			}
		} catch (err) {
			message.error("API Ping failed. " + getErrorMessage(err));
			console.log("API Ping failed");
			console.error(err);
			await logout();
		}
	};

	const isHavePermission = (...finds: EEmployeeRole[]) => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = finds.find((role) => auth.roles.includes(role));
		return find !== undefined;
	};
	const isAdminHQPlus = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = HQ_MANAGEMENT_ROLES.find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isAdminHQAndAdminOffices = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [...HQ_MANAGEMENT_ROLES, ...HQ_FINANCE_ROLES, ...OFFICE_MANAGEMENT_ROLES].find((role) =>
			auth.roles.includes(role)
		);
		return find !== undefined;
	}, [auth]);

	const isAdminHQWithFinancePlus = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [...HQ_FINANCE_ROLES, ...HQ_MANAGEMENT_ROLES].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isAdminHQWithOfficeManagementPlusAndAgent = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [
			...HQ_FINANCE_ROLES,
			...HQ_MANAGEMENT_ROLES,
			...OFFICE_MANAGEMENT_ROLES2,
			EEmployeeRole.manager_office,
			EEmployeeRole.admin_office,
			EEmployeeRole.agent,
		].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isAdminHQPlusOrOfficeManagement = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [
			...HQ_MANAGEMENT_ROLES,
			EEmployeeRole.franchise_owner,
			EEmployeeRole.business_owner,
			EEmployeeRole.manager_office,
			EEmployeeRole.admin_office,
		].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isFranchiseOrBusinessAdmin = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [EEmployeeRole.franchise_owner, EEmployeeRole.business_owner, EEmployeeRole.admin_office].find(
			(role) => auth.roles.includes(role)
		);
		return find !== undefined;
	}, [auth]);

	const isHQFinance = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = HQ_FINANCE_ROLES.find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isHQ = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [...HQ_MANAGEMENT_ROLES, ...HQ_FINANCE_ROLES].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isFOPlus = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [...HQ_MANAGEMENT_ROLES, EEmployeeRole.franchise_owner].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isOfficeOwner = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = OFFICE_MANAGEMENT_ROLES2.find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isFO = useMemo(() => {
		return auth.roles.includes(EEmployeeRole.franchise_owner);
	}, [auth.roles]);

	const isBO = useMemo(() => {
		return auth.roles.includes(EEmployeeRole.business_owner);
	}, [auth.roles]);

	const isHQManagerPlus = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = HQ_MANAGER_PLUS_ROLES.find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isAdminOfficePlus = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [...HQ_MANAGEMENT_ROLES, ...OFFICE_MANAGEMENT_ROLES].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isBOPlus = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [...HQ_MANAGEMENT_ROLES, EEmployeeRole.franchise_owner, EEmployeeRole.business_owner].find((role) =>
			auth.roles.includes(role)
		);
		return find !== undefined;
	}, [auth]);

	const isBOWithFinancePlus = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [
			...HQ_MANAGEMENT_ROLES,
			...HQ_FINANCE_ROLES,
			EEmployeeRole.franchise_owner,
			EEmployeeRole.business_owner,
		].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isOfficeManagementOnly = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = OFFICE_MANAGEMENT_ROLES.find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isOfficeManagementNonFinance = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = OFFICE_MANAGEMENT_ROLES.filter((v) => v !== EEmployeeRole.finance_office).find((role) =>
			auth.roles.includes(role)
		);
		return find !== undefined;
	}, [auth]);

	const isOfficeManagementWithFinance = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = OFFICE_MANAGEMENT_ROLES.filter((v) => v !== EEmployeeRole.admin_office).find((role) =>
			auth.roles.includes(role)
		);
		return find !== undefined;
	}, [auth]);

	const isAdminHQOfficeNonFinancePlus = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}

		let find = [
			...HQ_MANAGEMENT_ROLES,
			...OFFICE_MANAGEMENT_ROLES.filter((v) => v !== EEmployeeRole.finance_office),
		].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isHQAndOfficeManagementNonFinance = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [
			...HQ_MANAGEMENT_ROLES,
			...OFFICE_MANAGEMENT_ROLES.filter((v) => v !== EEmployeeRole.finance_office),
		].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isAgentOrAO = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [EEmployeeRole.admin_office, EEmployeeRole.agent].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isAgent = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [EEmployeeRole.agent].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isSuperAdmin = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [EEmployeeRole.admin].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isAdminHQ = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = HQ_MANAGEMENT_ROLES_EXC_SA.find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isAdminBlog = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = HQ_BLOG_ROLES.find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isBusinessFranchiseOrFinance = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		let find = [EEmployeeRole.business_owner, EEmployeeRole.franchise_owner, EEmployeeRole.finance_office].find(
			(role) => auth.roles.includes(role)
		);
		return find !== undefined;
	}, [auth]);

	const officeList = useMemo(() => {
		return (auth.employees.map((row) => row.office).filter((item) => item) as OfficeProperties[]) ?? [];
	}, [auth.employees]);

	const officeIdList = useMemo(() => {
		return officeList.map((v) => v.officeId) ?? [];
	}, [officeList]);

	const groupIdList = useMemo(() => {
		return (auth?.employees[0]?.groupMembers?.map((row) => row.groupId).filter((item) => item) as string[]) ?? [];
	}, [auth.employees]);

	const isPureRole = (role: EEmployeeRole) => {
		if (auth.status !== EAuthStatus.LOGGED_IN || auth.roles.length > 1) {
			return false;
		}

		if (auth.roles.length > 1) {
			return false;
		}

		return auth.roles[0] === role;
	};

	const isPureAgent = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}

		if (auth.roles.length > 1) {
			return false;
		}

		return auth.roles[0] === EEmployeeRole.agent;
	}, [auth]);

	const isAgentListing = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		if (auth.roles.includes(EEmployeeRole.agent)) {
			return auth.employees[0]?.allowSelfListing === true;
		}
		return true;
	}, [auth]);

	const isAdminHQWithOfficeFinancePlusAndAgent = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}

		let find = [
			...HQ_MANAGEMENT_ROLES,
			...HQ_FINANCE_ROLES,
			...OFFICE_MANAGEMENT_ROLES.filter((v) => v !== EEmployeeRole.admin_office),
			EEmployeeRole.agent,
		].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isAdminHQWithOfficeFinance = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}

		let find = [
			...HQ_MANAGEMENT_ROLES,
			...HQ_FINANCE_ROLES,
			...OFFICE_MANAGEMENT_ROLES.filter((v) => v !== EEmployeeRole.admin_office),
		].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isAdminHQOfficeFinancePlus = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}

		let find = [...HQ_FINANCE_ROLES, EEmployeeRole.finance_office].find((role) => auth.roles.includes(role));
		return find !== undefined;
	}, [auth]);

	const isAdminOrAdminHQ = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return false;
		}
		return [EEmployeeRole.admin, EEmployeeRole.admin_hq].some((role) => auth.roles.includes(role));
	}, [auth]);

	function roleColor(role: EEmployeeRole) {
		let isHQRole = [...HQ_MANAGEMENT_ROLES, ...HQ_FINANCE_ROLES, EEmployeeRole.admin_blog].some((r) => r === role);
		if (isHQRole) return "#065332";

		if (role === EEmployeeRole.business_owner) return "#2159D7";
		if (role === EEmployeeRole.franchise_owner) return "#071E52";
		if (
			role === EEmployeeRole.admin_office ||
			role === EEmployeeRole.finance_office ||
			role === EEmployeeRole.manager_office
		)
			return "#36B55F";

		let isAgentRole = [EEmployeeRole.agent].some((r) => r === role);
		if (isAgentRole) return "#18A4EA";

		return "default";
	}

	const showRoleUser = useMemo(() => {
		const uniqueRoles = Array.from(new Set(auth.roles));
		const roleLevels = [EMPLOYEE_ROLES_LEVELS.level1, EMPLOYEE_ROLES_LEVELS.level2, EMPLOYEE_ROLES_LEVELS.level3];

		for (const level of roleLevels) {
			for (const role of level) {
				if (uniqueRoles.includes(role as EEmployeeRole)) {
					return {
						roleName: EMPLOYEE_ROLES_MAPPING[role as EEmployeeRole],
						role,
					};
				}
			}
		}

		return {
			roleName: "Unknown Role",
			role: "role",
		};
	}, [auth]);

	const getThemeRole = useMemo(() => {
		const uniqueRoles = Array.from(new Set(auth.roles));
		const roleLevels = [EMPLOYEE_ROLES_LEVELS.level1, EMPLOYEE_ROLES_LEVELS.level2, EMPLOYEE_ROLES_LEVELS.level3];
		for (const level of roleLevels) {
			for (const role of level) {
				if (uniqueRoles.includes(role as EEmployeeRole)) {
					return {
						roleName: EMPLOYEE_ROLES_MAPPING[role as EEmployeeRole],
						role,
						theme: roleColor(role as EEmployeeRole),
					};
				}
			}
		}

		return {
			roleName: "Unknown Role",
			role: "role",
			theme: "#fff",
		};
	}, [auth]);

	const highestRole = useMemo(() => {
		const uniqueRoles = Array.from(new Set(auth.roles));
		const roleLevels = [EMPLOYEE_ROLES_LEVELS.level1, EMPLOYEE_ROLES_LEVELS.level2, EMPLOYEE_ROLES_LEVELS.level3];

		for (const level of roleLevels) {
			for (const role of level) {
				if (uniqueRoles.includes(role as EEmployeeRole)) {
					return role;
				}
			}
		}

		return "unknown_role";
	}, [auth]);

	const roleColorUser = useMemo(() => {
		if (auth.status !== EAuthStatus.LOGGED_IN) {
			return "default";
		}

		return roleColor(highestRole as EEmployeeRole);
	}, [auth]);

	return {
		isLoading,
		isLoggedIn: auth.status === EAuthStatus.LOGGED_IN,
		user: auth.user,
		employeeId: auth.employeeId,
		officeId: auth.officeId,
		employee: auth.employees[0],
		employees: auth.employees,
		roles: auth.roles,
		currentPosition: auth.currentPosition,
		login,
		logout,
		ping,
		saveProfile,
		isHavePermission,
		showRoleUser,
		roleColorUser,
		roleColor,
		getThemeRole,
		isAdminHQPlus,
		isAdminHQWithFinancePlus,
		isAdminHQWithOfficeFinancePlusAndAgent,
		isAdminHQWithOfficeManagementPlusAndAgent,
		isAdminHQPlusOrOfficeManagement,
		isHQFinance,
		isAdminHQAndAdminOffices,
		isFO,
		isBO,
		isFOPlus,
		isAdminHQ,
		isHQManagerPlus,
		isBOPlus,
		isBOWithFinancePlus,
		isAgentOrAO,
		isAgent,
		isSuperAdmin,
		isAdminOfficePlus,
		isOfficeManagementOnly,
		officeIdList,
		officeList,
		isOfficeOwner,
		isHQAndOfficeManagementNonFinance,
		isAdminHQOfficeNonFinancePlus,
		isOfficeManagementWithFinance,
		isAdminHQOfficeFinancePlus,
		isAdminHQWithOfficeFinance,
		groupIdList,
		isPureRole,
		isPureAgent,
		isAdminBlog,
		isFranchiseOrBusinessAdmin,
		isAgentListing,
		isBusinessFranchiseOrFinance,
		isAdminOrAdminHQ,
		isHQ,
		isOfficeManagementNonFinance,
		highestRole,
	};
}
